import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import Toolbar from '../components/Toolbar';
import AssetGrid from '../components/AssetGrid';
import CreateModal from '../components/CreateModal';
import CreateTeamModal from '../components/CreateTeamModal';
import TeamMembersModal from '../components/TeamMembersModal';
import FormBuilder from '../components/FormBuilder';
import AssistantChat from '../components/AssistantChat';
import SupportChatWidget from '../components/SupportChatWidget';
import AgreementsInbox from '../components/AgreementsInbox';
import { EMPTY_FILTERS } from '../components/FilterDropdown';
import { AssetsAPI, TeamsAPI, LabelsAPI } from '../api';
import { useAuth } from '../context/AuthContext';

const VIEW_TITLES = { all: 'All', favorites: 'Favorites', trash: 'Trash', shared: 'Shared with Me', label: 'Label' };

function useDebounced(value, delay) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
  return v;
}

export default function Workspace() {
  const { user } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [membersTeamId, setMembersTeamId] = useState(null);
  const [previewAsset, setPreviewAsset] = useState(null);
  const [layout, setLayout] = useState('grid');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState({ field: 'updated_at', order: 'desc' });
  const [view, setView] = useState('all');
  const [assets, setAssets] = useState([]);
  const [teams, setTeams] = useState([]);
  const [labels, setLabels] = useState([]);
  const [currentLabel, setCurrentLabel] = useState(null);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [trashCount, setTrashCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const debouncedSearch = useDebounced(search, 250);

  const params = useMemo(() => {
    const p = { view: view === 'label' || view === 'agreements' ? 'all' : view, sort: sort.field, order: sort.order };
    if (view === 'label' && currentLabel) p.label_id = currentLabel;
    if (debouncedSearch.trim()) p.q = debouncedSearch.trim();
    if (view === 'all' && currentTeam) p.team_id = currentTeam;
    else if (filters.team_id) p.team_id = filters.team_id;
    if (filters.types.length) p.types = filters.types.join(',');
    if (filters.status) p.status = filters.status;
    if (filters.starred) p.starred = filters.starred;
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    return p;
  }, [view, sort, debouncedSearch, currentTeam, filters, currentLabel]);

  const fetchAssets = useCallback(async () => {
    try {
      setAssets(await AssetsAPI.list(params));
    } catch (e) {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [params]);

  const fetchTrashCount = useCallback(async () => {
    try { setTrashCount((await AssetsAPI.list({ view: 'trash' })).length); } catch (e) { console.error('[workspace] trash count failed', e); }
  }, []);

  const fetchTeams = useCallback(async () => {
    try { setTeams(await TeamsAPI.list()); } catch (e) { toast.error('Failed to load teams'); }
  }, []);

  const fetchLabels = useCallback(async () => {
    try { setLabels(await LabelsAPI.list()); } catch (e) { toast.error('Failed to load labels'); }
  }, []);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);
  useEffect(() => { fetchTeams(); fetchTrashCount(); fetchLabels(); }, [fetchTeams, fetchTrashCount, fetchLabels]);

  const refreshAll = () => { fetchAssets(); fetchTrashCount(); };

  const handleCreateLabel = async (payload) => {
    try { const l = await LabelsAPI.create(payload); setLabels((prev) => [...prev, l]); toast.success(`Label "${l.name}" created`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create label'); }
  };
  const handleUpdateLabel = async (id, payload) => {
    try { const l = await LabelsAPI.update(id, payload); setLabels((prev) => prev.map((x) => (x.id === id ? l : x))); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to update label'); }
  };
  const handleDeleteLabel = async (id) => {
    if (!window.confirm('Delete this label? Assets will keep existing, only the tag is removed.')) return;
    try {
      await LabelsAPI.remove(id);
      setLabels((prev) => prev.filter((x) => x.id !== id));
      setAssets((prev) => prev.map((a) => ({ ...a, label_ids: (a.label_ids || []).filter((x) => x !== id) })));
      if (view === 'label' && currentLabel === id) { setView('all'); setCurrentLabel(null); }
      toast.success('Label deleted');
    } catch (e) { toast.error('Failed to delete label'); }
  };
  const handleDropOnLabel = async (assetId, labelId) => {
    try {
      const updated = await LabelsAPI.addToAsset(assetId, labelId);
      setAssets((prev) => prev.map((a) => (a.id === assetId ? updated : a)));
      const l = labels.find((x) => x.id === labelId);
      toast.success(`Tagged with "${l?.name}"`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add label'); }
  };
  const handleRemoveLabel = async (assetId, labelId) => {
    try {
      const updated = await LabelsAPI.removeFromAsset(assetId, labelId);
      if (view === 'label' && currentLabel === labelId) setAssets((prev) => prev.filter((a) => a.id !== assetId));
      else setAssets((prev) => prev.map((a) => (a.id === assetId ? updated : a)));
    } catch (e) { toast.error('Failed to remove label'); }
  };
  const selectLabel = (id) => { setCurrentLabel(id); setView('label'); };

  const handleAssetCreated = (asset) => { if (view === 'all') setAssets((prev) => [asset, ...prev]); else fetchAssets(); };

  const handleStar = async (id, starred) => {
    setAssets((prev) => prev.map((a) => (a.id === id ? { ...a, starred } : a)));
    try { await AssetsAPI.update(id, { starred }); if (view === 'favorites') fetchAssets(); } catch (e) { fetchAssets(); }
  };

  const handleRename = async (id, name) => {
    setAssets((prev) => prev.map((a) => (a.id === id ? { ...a, name } : a)));
    try { await AssetsAPI.update(id, { name }); } catch (e) { fetchAssets(); }
  };

  const handleDelete = async (id) => {
    const asset = assets.find((a) => a.id === id);
    setAssets((prev) => prev.filter((a) => a.id !== id));
    try {
      await AssetsAPI.remove(id);
      setTrashCount((c) => c + 1);
      toast.success(`"${asset?.name}" moved to trash`, {
        action: { label: 'Undo', onClick: () => AssetsAPI.restore(id).then(refreshAll) },
      });
    } catch (e) { toast.error('Failed to delete'); fetchAssets(); }
  };

  const handleRestore = async (id) => {
    setAssets((prev) => prev.filter((a) => a.id !== id));
    try { await AssetsAPI.restore(id); setTrashCount((c) => Math.max(0, c - 1)); toast.success('Asset restored'); } catch (e) { toast.error('Failed to restore'); fetchAssets(); }
  };

  const handlePermanentDelete = async (id) => {
    if (!window.confirm('Permanently delete this asset and all its submissions? This cannot be undone.')) return;
    setAssets((prev) => prev.filter((a) => a.id !== id));
    try { await AssetsAPI.permanentDelete(id); setTrashCount((c) => Math.max(0, c - 1)); toast.success('Permanently deleted'); } catch (e) { toast.error('Failed to delete'); fetchAssets(); }
  };

  const handleEmptyTrash = async () => {
    if (!window.confirm(`Permanently delete all ${assets.length} items in trash? This cannot be undone.`)) return;
    try { await AssetsAPI.emptyTrash(); setAssets([]); setTrashCount(0); toast.success('Trash emptied'); } catch (e) { toast.error('Failed to empty trash'); }
  };

  const handleDuplicate = async (id) => {
    try { const copy = await AssetsAPI.duplicate(id); setAssets((prev) => [copy, ...prev]); toast.success('Duplicated'); } catch (e) { toast.error('Failed to duplicate'); }
  };

  const handleCreateTeam = async (payload) => {
    const team = await TeamsAPI.create(payload);
    setTeams((prev) => [team, ...prev]);
    const pending = team.members.filter((m) => m.status === 'pending').length;
    toast.success(pending ? `Team created · ${pending} invitation${pending > 1 ? 's' : ''} pending` : 'Team created');
  };

  const handleDeleteTeam = async (id) => {
    if (!window.confirm('Delete this team? Assets will be moved to your personal workspace.')) return;
    try {
      await TeamsAPI.remove(id);
      setTeams((prev) => prev.filter((t) => t.id !== id));
      if (currentTeam === id) setCurrentTeam(null);
      toast.success('Team deleted');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete team'); }
  };

  const handleTeamUpdated = (updated) => setTeams((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));

  const activeTeam = teams.find((t) => t.id === currentTeam);
  const activeLabel = labels.find((l) => l.id === currentLabel);
  const membersTeam = teams.find((t) => t.id === membersTeamId);
  const isTrash = view === 'trash';
  const isAgreements = view === 'agreements';
  const title = view === 'all' && activeTeam ? activeTeam.name : view === 'label' ? activeLabel?.name || 'Label' : VIEW_TITLES[view];
  const subtitle = isTrash ? ' · Items are permanently deleted after 30 days'
    : view === 'shared' ? ' · Assets teammates created in your shared team workspaces'
    : view === 'label' ? ' · Drag more assets onto this label in the sidebar to tag them'
    : activeTeam?.description && view === 'all' ? ` · ${activeTeam.description}` : '';

  return (
    <div className="h-screen w-screen flex flex-col bg-[#F5F6FB] overflow-hidden font-sans" data-testid="workspace">
      <TopBar onMenu={() => setSidebarOpen(true)} />
      <div className="flex-1 flex overflow-hidden">
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onCreate={() => { setCreateOpen(true); setSidebarOpen(false); }}
          teams={teams}
          onCreateTeam={() => setTeamModalOpen(true)}
          currentTeam={currentTeam}
          onSelectTeam={setCurrentTeam}
          onDeleteTeam={handleDeleteTeam}
          view={view}
          onSelectView={(v) => { setView(v); setSidebarOpen(false); }}
          trashCount={trashCount}
          currentUserId={user?.user_id}
          isAdmin={!!user?.is_admin}
          labels={labels}
          currentLabel={currentLabel}
          onSelectLabel={selectLabel}
          onCreateLabel={handleCreateLabel}
          onUpdateLabel={handleUpdateLabel}
          onDeleteLabel={handleDeleteLabel}
          onDropOnLabel={handleDropOnLabel}
        />
        <main className="flex-1 flex flex-col overflow-hidden">
          {isAgreements ? (
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              <h1 className="text-[20px] md:text-[22px] font-bold text-[#0A1551] mb-1" data-testid="workspace-title">Investor Agreements</h1>
              <AgreementsInbox />
            </div>
          ) : (
          <>
          <Toolbar view={layout} setView={setLayout} search={search} setSearch={setSearch}
            filters={filters} setFilters={setFilters} sort={sort} setSort={setSort} teams={teams}
            hideTeamFilter={view === 'all' && !!currentTeam} />
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h1 className="text-[20px] md:text-[22px] font-bold text-[#0A1551] flex items-center gap-2" data-testid="workspace-title">
                  {view === 'all' && activeTeam && <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: activeTeam.color }}></span>}
                  {view === 'label' && activeLabel && <span className="w-3 h-3 rounded-full" style={{ backgroundColor: activeLabel.color }}></span>}
                  {title}
                </h1>
                <p className="text-[13px] text-[#6F76A7] mt-0.5" data-testid="workspace-count">
                  {assets.length} items{subtitle}
                </p>
              </div>
              {isTrash && assets.length > 0 && (
                <button onClick={handleEmptyTrash} data-testid="empty-trash-button" className="flex items-center gap-2 px-3 py-2 rounded-md border border-[#E5387B]/40 text-[13px] font-semibold text-[#E5387B] hover:bg-[#FEF2F2]">
                  <Trash2 className="w-4 h-4" /> Empty trash
                </button>
              )}
              {view === 'all' && activeTeam && (
                <button onClick={() => setMembersTeamId(activeTeam.id)} data-testid="team-members-button" className="flex items-center gap-2 group">
                  <div className="flex items-center -space-x-2">
                    {activeTeam.members.slice(0, 5).map((m) => (
                      <div key={m.email} title={`${m.name || m.email} · ${m.status === 'pending' ? 'Pending' : m.role}`}
                        className={`w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-[12px] font-bold text-white ${m.status === 'pending' ? 'opacity-50' : ''}`}
                        style={{ backgroundColor: m.avatar_color || activeTeam.color }}>
                        {(m.name || m.email).charAt(0).toUpperCase()}
                      </div>
                    ))}
                    {activeTeam.members.length > 5 && (
                      <div className="w-8 h-8 rounded-full border-2 border-white bg-[#E3E5F5] flex items-center justify-center text-[11px] font-bold text-[#0A1551]">+{activeTeam.members.length - 5}</div>
                    )}
                  </div>
                  <span className="flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#D5D7ED] text-[13px] font-medium text-[#0A1551] group-hover:bg-white"><UserPlus className="w-3.5 h-3.5" /> Invite</span>
                </button>
              )}
            </div>
            <AssetGrid
              assets={assets}
              view={layout}
              loading={loading}
              trashMode={isTrash}
              labels={labels}
              currentUserId={user?.user_id}
              onRemoveLabel={handleRemoveLabel}
              onStar={handleStar}
              onRename={handleRename}
              onDelete={handleDelete}
              onRestore={handleRestore}
              onPermanentDelete={handlePermanentDelete}
              onDuplicate={handleDuplicate}
              onPreview={setPreviewAsset}
            />
          </div>
          </>
          )}
        </main>
      </div>

      <CreateModal open={createOpen} onClose={() => setCreateOpen(false)} teamId={view === 'all' ? currentTeam : null} onAssetCreated={handleAssetCreated} />
      <CreateTeamModal open={teamModalOpen} onClose={() => setTeamModalOpen(false)} onCreate={handleCreateTeam} />
      {membersTeam && <TeamMembersModal team={membersTeam} onClose={() => setMembersTeamId(null)} onUpdated={handleTeamUpdated} />}
      <FormBuilder
        open={!!previewAsset}
        onClose={() => setPreviewAsset(null)}
        asset={previewAsset}
        onUpdated={(updated) => {
          setAssets((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
          setPreviewAsset(updated);
        }}
      />
      <AssistantChat />
      <SupportChatWidget />
    </div>
  );
}
